// Disputes: a chargeback, and whether the stylist still gets paid for it.
//
// The webhook already records disputes — it saves what the booking was before
// so a won dispute restores it rather than guessing at Completed, and writes a
// chargeback line to the ledger. What there was nowhere to do was see them
// together or stop a stylist being paid for a session the client has charged
// back, which otherwise leaves LIVLIFE out of pocket twice.
//
// Holding is reversible on purpose. Most disputes are won, and a stylist should
// not be punished for a client's bank.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Alert, Chip, Table, TableBody, TableCell, TableHead,
  TableRow, Button, TextField, CircularProgress, Tooltip,
} from '@mui/material';
import { supabase } from './supabaseClient';

const usd = (cents) => (cents == null ? '—' : `$${(Number(cents) / 100).toFixed(2)}`);

export default function DisputesPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [reason, setReason] = useState({});
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('dispute_cases').select('*').order('booking_date', { ascending: false });
    if (error) setMessage({ severity: 'error', text: error.message });
    setRows(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (bookingId, hold) => {
    const why = (reason[bookingId] || '').trim();
    if (!why) {
      setMessage({ severity: 'warning', text: 'Say why — it goes on the record against your name.' });
      return;
    }
    setBusy(bookingId);
    setMessage(null);
    const { error } = await supabase.rpc(
      hold ? 'admin_hold_stylist_earning' : 'admin_release_stylist_earning',
      { p_booking_id: bookingId, p_reason: why },
    );
    if (error) {
      setMessage({ severity: 'error', text: error.message });
    } else {
      setReason({ ...reason, [bookingId]: '' });
      setMessage({
        severity: 'success',
        text: hold ? 'Payout held. The stylist cannot cash this out.' : 'Payout released.',
      });
      await load();
    }
    setBusy(null);
  };

  const earningChip = (r) => {
    if (!r.earning_status) return <Chip size="small" label="no earnings" />;
    if (r.earning_status === 'pending') return <Chip size="small" color="warning" label="held" />;
    if (r.earning_status === 'available') return <Chip size="small" color="success" label="available" />;
    return <Chip size="small" label={r.earning_status} />;
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 0.5 }}>Disputes</Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        Bookings the client has charged back, or that Stripe has marked disputed. Holding a payout stops
        the stylist cashing out that session while it is contested; releasing puts it back. Both are
        recorded against your name.
      </Typography>

      {message && <Alert severity={message.severity} sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>}

      {loading ? <CircularProgress /> : rows.length === 0 ? (
        <Alert severity="success">
          No disputes. Nothing has been charged back and no booking is marked disputed.
        </Alert>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Booking</TableCell>
              <TableCell>Client</TableCell>
              <TableCell>Stylist</TableCell>
              <TableCell align="right">Charged</TableCell>
              <TableCell align="right">Charged back</TableCell>
              <TableCell align="right">Stylist&rsquo;s share</TableCell>
              <TableCell>Payout</TableCell>
              <TableCell>Why / action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.booking_id}>
                <TableCell>
                  <Typography variant="caption" sx={{ display: 'block' }}>{r.service_title}</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {r.booking_date ? new Date(r.booking_date).toLocaleDateString() : '—'}
                    {r.status_before_dispute ? ` · was ${r.status_before_dispute}` : ''}
                  </Typography>
                </TableCell>
                <TableCell>{r.client_phone}</TableCell>
                <TableCell>{r.stylist_phone}</TableCell>
                <TableCell align="right">{usd(r.amount_cents)}</TableCell>
                <TableCell align="right">{usd(r.chargeback_cents)}</TableCell>
                <TableCell align="right">{usd(r.earning_cents)}</TableCell>
                <TableCell>{earningChip(r)}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      size="small"
                      placeholder="Reason"
                      value={reason[r.booking_id] || ''}
                      onChange={(e) => setReason({ ...reason, [r.booking_id]: e.target.value })}
                      sx={{ minWidth: 170 }}
                    />
                    {r.earning_status === 'pending' ? (
                      <Button size="small" variant="outlined" disabled={busy === r.booking_id}
                              onClick={() => act(r.booking_id, false)}>
                        Release
                      </Button>
                    ) : (
                      <Tooltip title={['paid', 'processing'].includes(r.earning_status)
                        ? 'Already paid or in flight — recover it through Stripe, not here' : ''}>
                        <span>
                          <Button size="small" variant="outlined" color="warning"
                                  disabled={busy === r.booking_id || ['paid', 'processing'].includes(r.earning_status) || !r.earning_status}
                                  onClick={() => act(r.booking_id, true)}>
                            Hold payout
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 3 }}>
        Evidence is still submitted in the Stripe dashboard. Uploading it from here would mean sending
        files to Stripe&rsquo;s dispute API from an edge function, which is not built yet.
      </Typography>
    </Box>
  );
}
